/**
 * Currency Utilities
 * Provides currency detection, validation, and formatting utilities for frontend
 */

export enum Currency {
  USD = 'USD',
  GBP = 'GBP',
  EUR = 'EUR',
  CAD = 'CAD',
  AUD = 'AUD',
}

export const SUPPORTED_CURRENCIES: Currency[] = [
  Currency.USD,
  Currency.GBP,
  Currency.EUR,
  Currency.CAD,
  Currency.AUD,
];

export const DEFAULT_CURRENCY = Currency.USD;

/**
 * Validates if a currency string is supported
 */
export function isValidCurrency(currency: string): currency is Currency {
  return SUPPORTED_CURRENCIES.includes(currency as Currency);
}

/**
 * Maps browser locale to currency
 * Examples:
 * - "en-GB" -> GBP
 * - "en-US" -> USD
 * - "en-CA" -> CAD
 * - "en-AU" -> AUD
 * - "de-DE", "fr-FR", etc. -> EUR
 */
export function mapLocaleToCurrency(locale: string): Currency | null {
  if (!locale) {
    return null;
  }

  const normalizedLocale = locale.toLowerCase().trim();

  // UK
  if (normalizedLocale.startsWith('en-gb')) {
    return Currency.GBP;
  }

  // Canada
  if (normalizedLocale.startsWith('en-ca')) {
    return Currency.CAD;
  }

  // Australia
  if (normalizedLocale.startsWith('en-au')) {
    return Currency.AUD;
  }

  // European Union countries
  const euLocales = [
    'de', // German
    'fr', // French
    'es', // Spanish
    'it', // Italian
    'nl', // Dutch
    'pl', // Polish
    'pt', // Portuguese
    'ro', // Romanian
    'hu', // Hungarian
    'cs', // Czech
    'sk', // Slovak
    'sl', // Slovenian
    'bg', // Bulgarian
    'hr', // Croatian
    'et', // Estonian
    'lv', // Latvian
    'lt', // Lithuanian
    'mt', // Maltese
    'fi', // Finnish
    'el', // Greek
    'sv', // Swedish
    'da', // Danish
  ];

  for (const euLocale of euLocales) {
    if (normalizedLocale.startsWith(`${euLocale}-`)) {
      return Currency.EUR;
    }
  }

  // Default to null (will fallback to USD)
  return null;
}

/**
 * Detects user's currency from browser locale or cookie
 * Falls back to USD if currency cannot be determined
 */
export function detectUserCurrency(): Currency {
  if (typeof window === 'undefined') {
    return DEFAULT_CURRENCY;
  }

  // Check localStorage for saved preference (highest priority)
  const saved = localStorage.getItem('preferredCurrency');
  if (saved && isValidCurrency(saved)) {
    return saved as Currency;
  }

  // Check cookie (legacy; currency is primarily set client-side before hydration)
  const cookieCurrency = getCookie('userCurrency');
  if (cookieCurrency && isValidCurrency(cookieCurrency)) {
    return cookieCurrency as Currency;
  }

  // Detect from browser locale (fallback)
  const locale = navigator.language || (navigator.languages && navigator.languages[0]) || 'en-US';
  const detected = mapLocaleToCurrency(locale);
  const resolved = detected ?? DEFAULT_CURRENCY;
  try {
    document.cookie = `userCurrency=${resolved};path=/;max-age=31536000;SameSite=Lax`;
    localStorage.setItem('preferredCurrency', resolved);
  } catch {
    // ignore persistence failures
  }
  return resolved;
}

/**
 * Helper to read cookies in browser
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

/**
 * Formats an exact money amount for checkout totals.
 * Whole amounts hide the decimals (£835). Amounts with pence keep two decimals (£124.50).
 */
export function formatCurrencyExact(amount: number, currency: Currency): string {
  const safe = Math.round(Math.max(0, Number(amount) || 0) * 100) / 100;
  const whole = Math.abs(safe - Math.round(safe)) < 0.001;
  const fractionDigits = whole ? 0 : 2;
  const symbols: Record<Currency, string> = {
    [Currency.USD]: '$',
    [Currency.GBP]: '£',
    [Currency.EUR]: '€',
    [Currency.CAD]: 'C$',
    [Currency.AUD]: 'A$',
  };
  const plain = whole ? String(Math.round(safe)) : safe.toFixed(2);
  if (typeof window === 'undefined') {
    return `${symbols[currency]}${plain}`;
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: false,
    }).format(safe);
  } catch {
    return `${symbols[currency]}${plain}`;
  }
}

/**
 * Formats currency amount with proper symbol and formatting
 * Prices are rounded UP to the next whole number (ceiling)
 */
export function formatCurrency(amount: number, currency: Currency): string {
  // Round up to next whole number
  const roundedAmount = Math.ceil(amount);
  
  if (typeof window === 'undefined') {
    // Server-side fallback
    const symbols: Record<Currency, string> = {
      [Currency.USD]: '$',
      [Currency.GBP]: '£',
      [Currency.EUR]: '€',
      [Currency.CAD]: 'C$',
      [Currency.AUD]: 'A$',
    };
    return `${symbols[currency]}${roundedAmount}`;
  }

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: false, // Disable thousand separators (commas)
    });

    return formatter.format(roundedAmount);
  } catch (error) {
    // Fallback if Intl.NumberFormat fails
    const symbols: Record<Currency, string> = {
      [Currency.USD]: '$',
      [Currency.GBP]: '£',
      [Currency.EUR]: '€',
      [Currency.CAD]: 'C$',
      [Currency.AUD]: 'A$',
    };
    return `${symbols[currency]}${roundedAmount}`;
  }
}

/**
 * Gets currency symbol without amount
 */
export function getCurrencySymbol(currency: Currency): string {
  const symbols: Record<Currency, string> = {
    [Currency.USD]: '$',
    [Currency.GBP]: '£',
    [Currency.EUR]: '€',
    [Currency.CAD]: 'C$',
    [Currency.AUD]: 'A$',
  };
  return symbols[currency];
}

/**
 * Saves user's preferred currency to localStorage + cookie (cookie enables matching SSR).
 */
export function savePreferredCurrency(currency: Currency): void {
  if (typeof window !== 'undefined') {
    const previous = localStorage.getItem('preferredCurrency');
    localStorage.setItem('preferredCurrency', currency);
    try {
      document.cookie = `userCurrency=${currency};path=/;max-age=31536000;SameSite=Lax`;
    } catch {
      // ignore cookie write failures
    }
    (window as Window & { __USER_CURRENCY__?: string }).__USER_CURRENCY__ = currency;
    window.dispatchEvent(new Event('techxeed-currency-change'));
    if (previous !== currency) {
      void import("@/lib/analytics/ga4-ecommerce").then(({ ga4CurrencySwitch }) => {
        ga4CurrencySwitch({
          currency,
          previous_currency: previous ?? undefined,
        });
      });
    }
  }
}

/**
 * Converts affiliate commission value from source currency to target currency using live forex rates
 * This is used ONLY for affiliate commissions, which use live forex conversion
 * @param amount - The commission amount in source currency
 * @param fromCurrency - The source currency (currency the commission was stored in)
 * @param toCurrency - The target currency (user's preferred currency)
 * @param forexRates - Live forex rates from API (rates are from USD to each currency)
 * @returns The converted amount in target currency, rounded up to whole number
 */
export function convertAffiliateCommission(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency,
  forexRates: Record<Currency, number>,
): number {
  if (fromCurrency === toCurrency) {
    return Math.ceil(amount);
  }

  // Convert from source currency to USD first
  const usdAmount = fromCurrency === Currency.USD 
    ? amount 
    : amount / (forexRates[fromCurrency] || 1);

  // Then convert from USD to target currency
  const convertedAmount = toCurrency === Currency.USD
    ? usdAmount
    : usdAmount * (forexRates[toCurrency] || 1);

  // Round up to whole number (consistent with price rounding)
  return Math.ceil(convertedAmount);
}

